import { Controller } from "@hotwired/stimulus";

// Handles server-side sorting for data tables via Turbo Frames.
// Clicking a sortable column header navigates to a URL with sort params,
// and Turbo replaces the frame with sorted content from the server.
export default class extends Controller {
  static targets = ["header", "filterForm"];

  static values = {
    frame: { type: String, default: "" }, // Turbo Frame ID for navigation. If empty, falls back to full page reload.
    sortColumn: { type: String, default: "" }, // Currently sorted column name (used for toggle logic)
    sortDirection: { type: String, default: "asc" }, // Current sort direction ("asc" or "desc"). Default is "asc" to match the default sort direction in the Rails controller.
    columnParam: { type: String, default: "column" }, // URL parameter name for the sort column
    directionParam: { type: String, default: "direction" }, // URL parameter name for the sort direction
    filter: { type: String, default: "" }, // Active filter tab value — preserved when sorting inside a Turbo Frame
  };

  // Sort by the clicked column header
  sort(event) {
    const header = event.currentTarget;
    const column = header.dataset.column;

    if (!column) return;

    // Toggle direction if clicking the same column
    const direction = this.sortColumnValue === column && this.sortDirectionValue === "asc" ? "desc" : "asc";

    // Show loading spinner
    this.showLoading(header);

    // Navigate via Turbo Frame or full page
    this.navigate(column, direction);
  }

  // Show loading spinner on the clicked header
  showLoading(header) {
    const indicator = header.querySelector("[data-datatable-target='indicator']");
    const loader = header.querySelector("[data-datatable-target='loader']");

    if (indicator) indicator.classList.add("hidden");
    if (loader) loader.classList.remove("hidden");
  }

  // Navigate to sorted URL
  navigate(column, direction) {
    const url = new URL(window.location.href);

    // If there's a filter form, include all its current values in the URL
    // This preserves filters when sorting (form submission doesn't update browser URL)
    if (this.hasFilterFormTarget) {
      const formData = new FormData(this.filterFormTarget);

      // Clear existing filter params that might be stale in the URL
      // but keep non-form params (like page params from pagination)
      const formFieldNames = new Set();
      for (const [key] of formData.entries()) {
        formFieldNames.add(key);
      }

      // Remove old values for form fields
      for (const name of formFieldNames) {
        url.searchParams.delete(name);
      }

      // Add current form values
      for (const [key, value] of formData.entries()) {
        if (value !== "" && value !== null) {
          url.searchParams.append(key, value);
        }
      }
    }

    // Set the sort parameters (these will override any from the form's hidden fields)
    url.searchParams.set(this.columnParamValue, column);
    url.searchParams.set(this.directionParamValue, direction);

    // Preserve the active filter tab — the browser URL doesn't update when
    // switching tabs inside a Turbo Frame, so we carry it explicitly.
    if (this.filterValue) {
      url.searchParams.set("filter", this.filterValue);
    }

    if (this.frameValue) {
      const frame = document.getElementById(this.frameValue);
      if (frame) {
        frame.src = url.toString();
        return;
      }
    }

    window.location.href = url.toString();
  }
}